import { useState } from 'react';
import { TIPOS_DE_RECLAMO, type TipoDeReclamo, type TurnoDetalle } from '@medchoice/shared';
import { ApiError } from '../../api/client';
import { paciente } from '../../api/paciente';
import { usePedido } from '../../api/usePedido';
import { Enlace, irA } from '../../router';
import type { ScreenProps } from '../../routes/types';
import {
  AreaTexto, AvisoError, Boton, Cargando, Entrada, FalloDeCarga, Icono, InsigniaEstadoTurno, Modal, Selector, Tarjeta,
  useAvisos,
} from '../../ui';
import { fechaLarga, pesos } from '../../ui/format';

const MOTIVOS = ['Me surgió un imprevisto', 'Ya no lo necesito', 'Conseguí turno antes en otro lado', 'Problemas para llegar', 'Otro motivo'];
const TEXTO_RECLAMO: Record<TipoDeReclamo, string> = {
  atencion: 'La atención', cobro: 'Un cobro o la seña', plataforma: 'La plataforma', otro: 'Otra cosa',
};

const TEXTO_PAGO = { pendiente: 'en revisión', aprobado: 'acreditada', rechazado: 'anulada', reintegrado: 'devuelta' } as const;

export function TurnoDePaciente({ params }: ScreenProps) {
  const id = Number(params['id']);
  const pedido = usePedido(() => paciente.turno(id), [id]);
  if (pedido.estado === 'cargando') return <Cargando />;
  if (!pedido.datos) return <FalloDeCarga mensaje={pedido.error ?? 'No encontramos el turno'} alReintentar={pedido.recargar} />;
  return <Detalle turno={pedido.datos} alCambiar={pedido.recargar} />;
}

function Detalle({ turno: t, alCambiar }: { turno: TurnoDetalle; alCambiar: () => void }) {
  const [cancelando, setCancelando] = useState(false);
  const [reclamando, setReclamando] = useState(false);
  const abierto = t.estado === 'pendiente' || t.estado === 'confirmado';
  const profesional = `${t.profesional.nombre} ${t.profesional.apellido}`;

  return (
    <div className="detalle-turno">
      <Enlace a="/mis-turnos" className="detalle-turno__volver"><Icono nombre="chevron-left" tamano={16} /> Mis turnos</Enlace>
      <Tarjeta>
        <header className="detalle-turno__encabezado">
          <div>
            <span className="codigo">#{t.codigo}</span>
            <h1>{t.servicio.nombre}</h1>
            <p>con {profesional} · {t.profesional.especialidad}</p>
          </div>
          <InsigniaEstadoTurno estado={t.estado} tamano="grande" />
        </header>
        <dl className="reserva__resumen">
          <div><dt>Cuándo</dt><dd>{fechaLarga(t.fecha)}, de {t.hora_inicio} a {t.hora_fin}</dd></div>
          <div><dt>Dónde</dt><dd>{t.modalidad === 'videoconsulta' ? 'Por videoconsulta' : `${t.profesional.direccion}, ${t.profesional.barrio}`}</dd></div>
          {t.link_meet && abierto && <div><dt>Enlace</dt><dd><a href={t.link_meet} target="_blank" rel="noreferrer">{t.link_meet}</a></dd></div>}
          <div><dt>Precio de la consulta</dt><dd>{pesos(t.precio)}</dd></div>
          {t.pago && (
            <div><dt>Seña</dt><dd>{pesos(t.pago.monto)} · {t.estado === 'cancelado' && t.pago.estado === 'aprobado'
              ? 'no se devuelve: cancelaste con menos de 24 horas' : TEXTO_PAGO[t.pago.estado]}</dd></div>
          )}
          {t.motivo_cancelacion && <div><dt>Motivo de la cancelación</dt><dd>{t.motivo_cancelacion}</dd></div>}
          {t.resena && <div><dt>Tu reseña</dt><dd>{t.resena.calificacion} de 5 · {t.resena.comentario}</dd></div>}
        </dl>
        <div className="reserva__acciones">
          {abierto && <Boton variante="secundario" onClick={() => irA(`/reprogramar/${t.id_turno}`)}>Reprogramar</Boton>}
          {abierto && <Boton variante="peligro" onClick={() => setCancelando(true)}>Cancelar turno</Boton>}
          {t.estado === 'atendido' && !t.resena && <Boton onClick={() => irA(`/resena/${t.id_turno}`)}>Dejar reseña</Boton>}
        </div>
      </Tarjeta>
      <button type="button" className="boton-sutil detalle-turno__problema" onClick={() => setReclamando(true)}>
        ¿Tuviste un problema con este turno?
      </button>
      <ModalCancelacion turno={t} abierto={cancelando} alCerrar={() => setCancelando(false)} alCancelar={alCambiar} />
      <ModalReclamo turno={t} abierto={reclamando} alCerrar={() => setReclamando(false)} />
    </div>
  );
}

interface PropsModal { turno: TurnoDetalle; abierto: boolean; alCerrar: () => void }

function ModalCancelacion({ turno, abierto, alCerrar, alCancelar }: PropsModal & { alCancelar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [motivo, setMotivo] = useState('');
  const [otro, setOtro] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const texto = motivo === 'Otro motivo' ? otro.trim() : motivo;
  const pierdeSena = turno.pago?.estado === 'aprobado' && !turno.cancela_sin_cargo;

  const cancelar = async () => {
    setEnviando(true);
    try {
      const { reintegro } = await paciente.cancelar(turno.id_turno, texto);
      mostrarAviso({ tono: 'exito', titulo: 'Turno cancelado', cuerpo: reintegro ? 'Te devolvemos la seña en los próximos días.' : undefined });
      alCerrar();
      alCancelar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos cancelar el turno. Probá de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal abierto={abierto} alCerrar={alCerrar} titulo="Cancelar el turno"
      pie={<>
        <Boton variante="secundario" onClick={alCerrar}>Volver</Boton>
        <Boton variante="peligro" disabled={texto.length < 5} cargando={enviando} onClick={() => void cancelar()}>Confirmar cancelación</Boton>
      </>}>
      <AvisoError>{error}</AvisoError>
      {pierdeSena && (
        <AvisoError tono="aviso">Falta poco para el turno: si cancelás ahora, la seña de {pesos(turno.pago?.monto ?? 0)} no se devuelve.</AvisoError>
      )}
      <Selector etiqueta="¿Por qué cancelás?" marcador="Elegí un motivo" value={motivo} opciones={MOTIVOS}
        onChange={(e) => setMotivo(e.target.value)} />
      {motivo === 'Otro motivo' && (
        <Entrada etiqueta="Contanos el motivo" maxLength={300} value={otro} onChange={(e) => setOtro(e.target.value)} />
      )}
    </Modal>
  );
}

function ModalReclamo({ turno, abierto, alCerrar }: PropsModal) {
  const { mostrarAviso } = useAvisos();
  const [tipo, setTipo] = useState<TipoDeReclamo>('atencion');
  const [asunto, setAsunto] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const valido = asunto.trim().length >= 5 && descripcion.trim().length >= 20;

  const enviar = async () => {
    setEnviando(true);
    try {
      await paciente.reclamar({ tipo, asunto, descripcion, turno: turno.id_turno });
      mostrarAviso({ tono: 'exito', titulo: 'Recibimos tu reclamo', cuerpo: 'Lo seguís desde Mi cuenta.' });
      alCerrar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos enviar el reclamo. Probá de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal abierto={abierto} alCerrar={alCerrar} titulo="Contanos qué pasó"
      pie={<>
        <Boton variante="secundario" onClick={alCerrar}>Volver</Boton>
        <Boton disabled={!valido} cargando={enviando} onClick={() => void enviar()}>Enviar reclamo</Boton>
      </>}>
      <AvisoError>{error}</AvisoError>
      <Selector etiqueta="¿Sobre qué es?" value={tipo} onChange={(e) => setTipo(e.target.value as TipoDeReclamo)}
        opciones={TIPOS_DE_RECLAMO.map((t) => ({ valor: t, texto: TEXTO_RECLAMO[t] }))} />
      <Entrada etiqueta="Asunto" maxLength={100} value={asunto} onChange={(e) => setAsunto(e.target.value)} required />
      <AreaTexto etiqueta="Qué pasó" maxLength={2000} value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
        ayuda="Al menos 20 caracteres." required />
    </Modal>
  );
}
